<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-toolbar flat color="transparent">
          <v-toolbar-title>{{$t('Calendar.Header.brandCalendar')}}</v-toolbar-title>
          <v-divider vertical class="mx-5"></v-divider>
          <DataFetcher v-slot:default="{ items }" endpoint="/malls" class="mr-5">
            <v-select
              v-model="selectedMall"
              :items="items"
              :label="$t('Calendar.Label.mall')"
              item-text="name"
              color="primary"
              value="id"
              return-object
              hide-details
              style="max-width: 200px;"
            ></v-select>
          </DataFetcher>
          <BrandSelector
            v-if="selectedMall"
            v-model="selectedUser"
            :belongsToThisMall="this.selectedMall"
            class="mr-5"
            style="max-width: 200px;"
          />
          <div>
            <v-select v-if="selectedUser"
              v-model="selectedYear"
              :items="years.reverse()"
              :label="$t('Calendar.Label.year')"
              hide-details
              style="max-width: 150px;"
            ></v-select>
          </div>
          <v-spacer></v-spacer>
          <template v-if="edited">
            <v-btn @click="cancel" color="grey" class="mr-5" small text>
              <v-icon left>cancel</v-icon>
              {{$t('Global.Action.cancel')}}
            </v-btn>
            <v-btn @click="updateClosedDates" color="primary" small>
              <v-icon left>save</v-icon>
              {{$t('Global.Action.save')}}
            </v-btn>
          </template>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-slide-x-transition mode="out-in">
      <template v-if="selectedYear && selectedMall && !loading">
        <v-row dense v-if="calendar.length">
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="calendar"
              :items-per-page="-1"
              dense
              hide-default-footer
              hide-default-header
              class="elevation-1 py-2"
            >
              <template v-slot:header="{ props }">
                <thead class="v-data-table-header">
                  <tr>
                    <th v-for="header in props.headers" :key="header.text">
                      <span class="mr-3">{{header.text}}</span>
                      <v-btn x-small outlined @click="switchDayForAllWeeks(header.value)">{{allWeekdayOpenStates[header.value] ? 'Fermer tous' : 'Ouvrir tous'}}</v-btn>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:item="{ item, index }">
                <tr>
                  <template v-if="index === 0">
                    <td v-for="n in 7 - item.length" :key="'blank' + n" :class="{'gridRightBorder': n === 7 - item.length}"></td>
                  </template>
                  <td v-for="(day, index) in item" :key="day.dateFormatted" :class="{'gridRightBorder': index !== item.length - 1}">
                    <!-- If mall is closed : you can't open brand -->
                    <v-checkbox
                      v-if="!day.isOpen"
                      v-model="day.isOpen"
                      :label="day.dateFormatted"
                      hide-details
                      readonly
                      off-icon="domain"
                      color="grey"
                      class="mt-0 grey--text"
                    >
                      <template v-slot:append>
                        <v-tooltip bottom v-if="day.nbEntriesWithData > 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="primary"
                              dark
                              v-bind="attrs"
                              v-on="on"
                            >
                              keyboard
                            </v-icon>
                          </template>
                          <span>Données saisies pour {{dataFrequencyTooltipText}}</span>
                        </v-tooltip>
                        <span class="hidden-lg-and-down mt-1 ml-1">
                          {{$t('Global.Common.close')}}
                        </span>
                      </template>
                    </v-checkbox>

                    <!-- If mall is open : you can open/close brand -->
                    <v-checkbox
                      v-else
                      v-model="day.brandIsOpen"
                      :label="day.dateFormatted"
                      :error="!day.brandIsOpen"
                      color="secondary"
                      hide-details
                      class="mt-0"
                      @click="edited = true"
                    >
                      <template v-slot:append>
                        <v-tooltip bottom v-if="day.nbEntriesWithData > 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              color="primary"
                              dark
                              v-bind="attrs"
                              v-on="on"
                            >
                              keyboard
                            </v-icon>
                          </template>
                          <span>Données saisies pour {{dataFrequencyTooltipText}}</span>
                        </v-tooltip>
                        <span class="hidden-lg-and-down mt-1 ml-1">{{day.brandIsOpen ? $t('Global.Common.open') : $t('Global.Common.close')}}</span>
                      </template>
                    </v-checkbox>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <v-alert type="warning" text>
                  {{$t('Calendar.Message.notGeneratedYet', {year: selectedYear, mall: selectedMall.name})}}
                </v-alert>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row v-else justify="center">
          <v-col cols="12" xl="6">
            <v-alert type="info" prominent icon="event_busy">
              {{ $t('Calendar.Message.notGeneratedYet', {year: selectedYear, mall: selectedMall.name}) }}.
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </v-slide-x-transition>
  </v-container>
</template>

<script>
import DataFetcher from '@/components/Utils/DataFetcher'
import BrandSelector from '@/components/Utils/BrandSelector'
import Calendar from '@/services/calendar'
import CalendarHelpers from '@/mixins/CalendarHelpers'
import { frequencyKeys } from '@/config/fixedValues';
import { eachYearOfInterval, getYear, parseISO, formatISO, isSameDay, addYears, isBefore } from 'date-fns'
import { mapState } from 'vuex'
import { getDay } from 'date-fns/fp'

export default {
  name: 'BrandCalendar',
  components: { DataFetcher, BrandSelector },
    mixins: [ CalendarHelpers ],
  data: () => ({
    edited: false,
    selectedMall: null,
    selectedUser: null,
    selectedYear: null,
    calendar: [],
    loading: false
  }),
  computed: {
    ...mapState('auth', [
      'user'
    ]),
    headers () {
      return [
        {text: 'Lundi', value: 1, sortable: false},
        {text: 'Mardi', value: 2, sortable: false},
        {text: 'Mercredi', value: 3, sortable: false},
        {text: 'Jeudi', value: 4, sortable: false},
        {text: 'Vendredi', value: 5, sortable: false},
        {text: 'Samedi', value: 6, sortable: false},
        {text: 'Dimanche', value: 0, sortable: false}
      ]
    },
    years () {
      if (!this.selectedUser.mall) return []
      return eachYearOfInterval({
        start: new Date(this.selectedUser.openingDate),
        end: this.selectedUser.closingDate ? new Date(this.selectedUser.closingDate) : addYears(new Date(), 1)
      })
      .map(year => getYear(year))
    },
    allWeekdayOpenStates () {
      const weekdays = {
        0: true,
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true
      }

      for (const day of this.calendar.flat()) {
        const weekday = getDay(parseISO(day.date))
        if (!day.brandIsOpen) {
          weekdays[weekday] = false
        }
      }

      return weekdays
    },
    dataFrequencyTooltipText () {
      let text = ''
      switch (this.selectedUser.inputFrequency) {
        case frequencyKeys.QUOTIDIEN:
          text = 'ce jour'
          break
        case frequencyKeys.HEBDOMADAIRE:
          text = 'cette semaine'
          break
        case frequencyKeys.MENSUEL:
          text = 'ce mois'
          break
      }
      return text
    }
  },
  watch: {
    selectedYear (value) {
      if (value) {
        this.edited = false
        this.loadCalendar(value)
      }
    },
    selectedMall (value) {
      if (value) {
        this.selectedUser = null
        this.selectedYear = null
      }
    },
    selectedUser (value) {
      if (value) {
        const defaultYear = this.years.includes(getYear(new Date())) ? getYear(new Date()) : this.years[this.years.length - 1]
        if (this.selectedYear === defaultYear) {
          this.loadCalendar(defaultYear)
        } else {
          this.selectedYear = defaultYear
        }
      }
    }
  },
  methods: {
    /**
     * If a day (ex: dayNumber = 1 = monday) is open in each weeks we close this day for all weeks
     * If a day is not open in each weeks we open this day for all weeks
     */
    switchDayForAllWeeks(dayNumber) {
      const switchTo = !this.allWeekdayOpenStates[dayNumber]
      for (const week of this.calendar) {
        for (const day of week) {
          if (getDay(parseISO(day.date)) === dayNumber && day.isOpen) day.brandIsOpen = switchTo
        }
      }
      this.edited = true
    },

    cancel () {
      this.edited = false
      this.loadCalendar(this.selectedYear)
    },

    async loadCalendar (year) {
      this.loading = true
      try {
        // load mall calendar and user brand override closed dates
        const userClosedDates = await Calendar.getUserClosingDates(this.selectedUser.id, year)
        const mallDates = await Calendar.getMallCalendar(this.selectedUser.mall.id, year, this.selectedUser.id)

        // hydrate mall calendar with user brand overrides
        const calendarDates = mallDates
          .map(cell => ({
            ...cell,
            dateFormatted: (new Date(cell.date)).toLocaleString('fr-FR', {day: 'numeric', month: 'short'}),
            brandIsOpen: !userClosedDates.some(closedDate => isSameDay(parseISO(cell.date), parseISO(closedDate.date))),
          }))
          // remove dates before user opening date
          .filter(cell => !isBefore(parseISO(cell.date), parseISO(this.selectedUser.openingDate)))
          // remove dates after user closing date
          .filter(cell => !this.selectedUser.closingDate || !isBefore(parseISO(this.selectedUser.closingDate), parseISO(cell.date)))

        this.calendar = this.generateCalendarGrid(calendarDates)
        this.loading = false
      } catch (error) {
        this.$store.dispatch('errorMessage', this.$t('Message.Error.read'))
      }
    },


    async updateClosedDates () {
      // Prepare a simple array with all user overrides (brand closed dates)
      const dates = this.calendar
        .flat()
        .filter(date => !date.brandIsOpen)
        .map(date => formatISO(parseISO(date.date), { representation: 'date' }))

      try {
        await Calendar.updateUserClosingDates(this.selectedUser.id, this.selectedYear, dates)
        this.$store.dispatch('successMessage', this.$t('Message.Success.update'))
      } catch (error) {
        this.$store.dispatch('errorMessage', this.$t('Message.Error.update'))
      }
    }
  },
  created () {
    this.selectedMall = this.user.mall
  }
}
</script>

<style scoped>
.gridRightBorder {
  border-right: thin solid rgba(0, 0, 0, 0.12);
}
</style>
